import { API_TBase } from "./API";
import { format } from "date-fns";

const DATETIME_FORMAT = "dd/MM/yyyy";

export type API_TTeammate = {
  email: string;
  permission: string;
};

export type TTaskSelect = {
  label: string;
  value: string | number;
};

export type API_TTeammates_Pending = {
  ID: number;
  email: string;
  permission: string;
  expire_date: string;
} & API_TBase;

export type TTeammates_Pending_Table = {
  id: number;
  email: string;
  expirationDate: string | Date;
  permission: string;
};

export type NullBool = {
  Bool: boolean;
  Valid: boolean;
};

export type API_TTeammates = {
  ID: number;
  compliance_admin: NullBool;
  first_name: string;
  last_name: string;
  email: string;
  permission: string;
  has_validation_passed: boolean;
} & API_TBase;

export type TTeammates_Table = {
  id: number;
  name: string;
  email: string;
  permission: string;
  complianceAdmin: boolean;
};

export type TTeammates_Status = {
  cases: TTeammates_Status_Case[];
  is_compliance_admin: boolean;
  is_has_cases: boolean;
  teammate_name: string;
  email: string;
};

export type TTeammates_Status_Case = {
  id: number;
  case_name: string;
  teammate_info: TTeammates_Status_Case_Info;
};

export type TTeammates_Status_Case_Info = {
  first_name: string;
  last_name: string;
  job_position: string;
};

export type TTeammates_Case_Table = {
  id: number;
  name: string;
  jobPosition: string;
  caseName: string;
};

export type TTeammates_Select = {
  label: string;
  value: number;
};

const capitalizeFirstLetter = (str: string): string => {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
};

const convertDatetime = (value: string, formatStr?: string) => {
  const datetime = new Date(value);
  const ts = datetime.getTime();

  const datetimeNow = new Date();
  const tsNow = datetimeNow.getTime();

  if (ts <= 0) return "-";

  if (ts < tsNow) return "Expired";

  return formatStr ? format(datetime, formatStr) : datetime;
};

const convertBoolToString = (section: NullBool) => {
  return section.Valid && section.Bool;
};

const convertToString = (section: string | number | null) => {
  return section?.toString() || "—";
};

export const convertTeammatesForTable = (a: API_TTeammates): TTeammates_Table => {
  return {
    id: a.ID,
    name: a.first_name && a.last_name ? `${a.first_name} ${a.last_name}` : "―",
    email: convertToString(a.email),
    permission: a.permission ? capitalizeFirstLetter(a.permission) : "―",
    complianceAdmin: convertBoolToString(a.compliance_admin),
  };
};

export const convertTeammatesPendingForTable = (
  a: API_TTeammates_Pending
): TTeammates_Pending_Table => {
  const formatStr = DATETIME_FORMAT;

  return {
    id: a.ID,
    email: convertToString(a.email),
    expirationDate: convertDatetime(a.expire_date, formatStr),
    permission: a.permission ? capitalizeFirstLetter(a.permission) : "―",
  };
};

export const convertTeammatesForSelect = (a: API_TTeammates): TTeammates_Select => {
  return {
    value: a.ID,
    label: `${a.first_name} ${a.last_name} | ${a.email}`,
  };
};

export const convertTeammatesCases = (a: TTeammates_Status_Case): TTeammates_Case_Table => {
  return {
    id: a.id,
    name:
      a.teammate_info.first_name && a.teammate_info.last_name
        ? `${a.teammate_info.first_name} ${a.teammate_info.last_name}`
        : "―",
    jobPosition: a.teammate_info.job_position ? `${a.teammate_info.job_position}` : "―",
    caseName: a.case_name ? `${a.case_name}` : "―",
  };
};

export type TCase_Redistribution = {
  case_id: number;
  user_id: number;
};

export type TTeammate_Delete_Params = {
  new_compliance_admin_id: number;
  case_redistribution: TCase_Redistribution[];
};
